/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from "react";
import { useLoadScript } from "@react-google-maps/api";
import config from "../config";
import { getCurrentOffset } from "./getCurrentOffset";

const libraries = ["places"];

const DropdownWithSearchInside = ({ auctionData, getTimezone, disabled }) => {
    const [selected, setSelected] = useState(auctionData?.location?.address || null);
    const [search, setSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [predictions, setPredictions] = useState([]);
    const containerRef = useRef(null);

    const defaultCities = [
        { label: "London, UK", timeZone: "Europe/London", latitude: 51.5072178, longitude: -0.1275862, 
            offset: getCurrentOffset("Europe/London") },
        { label: "New York, NY, USA", timeZone: "America/New_York", latitude: 40.7127753, longitude: -74.0059728,
            offset: getCurrentOffset("America/New_York")
        },
        { label: "Los Angeles, CA, USA", timeZone: "America/Los_Angeles", latitude: 34.0549076, longitude: -118.242643,
            offset: getCurrentOffset("America/Los_Angeles")
        },
    ];
    
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: config.GOOGLEAPIKEY,
        libraries,
    });

    const autocompleteService = useRef(null);
    const placesService = useRef(null);

    useEffect(()=>{
        if(auctionData?.location?.address!=="" && !selected) {
            setSelected(auctionData.location.address);
        }
    },[auctionData])

    useEffect(() => {
        if (isLoaded) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
    }, [isLoaded]);

    const handleSearch = (value) => {
        setSearch(value);
        if (value && autocompleteService.current) {
            autocompleteService.current.getPlacePredictions(
                {
                    input: value,
                    types: ["(cities)"],
                },
                (preds, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && preds ) {
                        setPredictions(preds);
                    } else {
                        setPredictions([]);
                    }
                }
            );
        } else {
            setPredictions([]);
        }
    };

    const handleSelectPrediction = async (prediction) => {
        if (!placesService.current) {
            const dummyMap = document.createElement("div");
            placesService.current = new window.google.maps.places.PlacesService(
                dummyMap
            );
        }

        placesService.current.getDetails(
            { placeId: prediction.place_id },
            async (place, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    getTimezone(lat, lng, prediction.description, setSelected);
                    setShowDropdown(false);
                    setSearch("");
                }
            }
        );
    };

    useEffect(() => {
        const clickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", clickOutside);
        return () => document.removeEventListener("mousedown", clickOutside);
    }, []); 

    return (
        <div className="relative w-full mt-1" ref={containerRef}>
            <div data-testid="time-zone-dropdown"
                className={`border p-3 text-start ${disabled ? 'border-[#c9c9cc] text-[#A1A1A9] bg-[#F4F4F5]' : 'cursor-pointer bg-white border-[#A1A1A9]'} text-[14px] 
                    ${selected ? 'text-[#343434]' : 'text-[#A1A1A9]'}`}
                onClick={() => {
                    if(disabled) return;
                    setShowDropdown(!showDropdown)
                }}
            >
                {selected || "Select timezone"}
            </div>
            <img src="/images/dropdown-icon.png" alt="dropdown-icon" 
                className={`absolute top-[20px] right-[15px] 
                            ${showDropdown?'rotate-180':'transform-none'}`}
                loading="lazy" />

            {showDropdown && (
                <div className="absolute bg-white border mt-1 rounded shadow-md w-full max-h-80 overflow-y-auto z-50">
                    <div className="sticky top-0 bg-white px-3 py-2">
                        <div className="relative w-full">
                            <input
                                data-testid="search-input"
                                id="search-input"
                                type="text"
                                className="w-full border px-2 py-1 rounded text-[14px] text-[#343434] border-[#D4D4D8] outline-none"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => handleSearch(e.target.value)}
                                autoFocus
                            />
                            <img 
                                src={`/images/search_icon.svg`} 
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" alt="img" />
                        </div>
                    </div>

                    <div>
                        {/* Show default if no search */}
                        {search === "" &&
                            defaultCities.map((item, index) => (
                                <div
                                    key={index}
                                    id={`default-city-${index}`}
                                    data-testid={`default-city-${index}`}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-[14px] text-[#343434]"
                                    onClick={() => {
                                        getTimezone(item.latitude, item.longitude, item.label, setSelected);
                                        setShowDropdown(false);
                                    }}
                                >
                                    {item.label} <span className="text-[#717179]" >({item.offset})</span>
                                </div>
                            ))
                        }

                        {/* Show Google predictions */}
                        {search !== "" &&
                            predictions.map((prediction, index) => (
                                <div data-testid={`prediction-${index}`}
                                    key={prediction.place_id}
                                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-[14px] text-[#343434]"
                                    onClick={() => handleSelectPrediction(prediction)}
                                >
                                    {prediction.description}
                                </div>
                            ))
                        }

                        {search !== "" && predictions.length === 0 && (
                            <div className="px-4 py-2 text-gray-500">No results</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DropdownWithSearchInside;
